import { ScrollArea, ScrollBar, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Text } from "@sentricmusic/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Database, RefreshCw, Settings, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useUIStateStore } from "@/store/uiStateStore";

import useAppStore from "../../store/appStateZustand";

type Account = {
  artistaccountcode: string;
  accountname?: string;
  cataloguecode: string;
};

type MyAccountsData = {
  data?: {
    artistaccounts?: Account[];
  };
};

type DevToolsProps = {

  setPreviousAccountData: (data: unknown) => void;
  FORCE_TUNECORE_MODE: boolean;
  setForceTunecoreMode: (value: boolean) => void;
  selectedAccount?: string;
  myAccounts?: MyAccountsData;
};

export function DevTools({
  setPreviousAccountData,
  FORCE_TUNECORE_MODE,
  setForceTunecoreMode,
  selectedAccount,
  myAccounts,
}: DevToolsProps) {
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(false);
  const [networkRequestCount, setNetworkRequestCount] = useState(0);
  const requestCountRef = useRef(0);
  const isTunecore = useUIStateStore(state => state.isTunecore);

  // Environment-based feature flags
  const isLocalDev = import.meta.env.VITE_STAGE === "local";
  const enableFetchOverride = isLocalDev; // Only enable fetch override in local dev
  const enableHeavyDebugging = isLocalDev; // Only enable heavy state serialization in local dev

  // Get config data
  const flags = useFlags();

  // Debounced toggle to prevent rapid clicking issues
  const debouncedToggleRef = useRef<NodeJS.Timeout | null>(null);
  const handleToggleExpanded = useCallback(() => {
    if (debouncedToggleRef.current) {
      clearTimeout(debouncedToggleRef.current);
    }
    debouncedToggleRef.current = setTimeout(() => {
      setIsExpanded(prev => !prev);
    }, 100); // 100ms debounce
  }, []);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debouncedToggleRef.current) {
        clearTimeout(debouncedToggleRef.current);
      }
    };
  }, []);

  // Track network requests in real-time (LOCAL DEV ONLY)
  useEffect(() => {
    if (!enableFetchOverride) {
      return; // Skip fetch override in deployed environments
    }

    // Override fetch to count dashboard-related requests
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const url = args[0]?.toString() || "";

      // Track dashboard-related API calls
      if (url.includes("getmyworks")
        || url.includes("getmywriters")
        || url.includes("top-gigs")
        || url.includes("top-spotify")) {
        requestCountRef.current++;
        setNetworkRequestCount(requestCountRef.current);
      }

      return originalFetch.apply(this, args);
    };

    // Cleanup function
    return () => {
      window.fetch = originalFetch;
    };
  }, [enableFetchOverride]); // Include enableFetchOverride in dependencies

  const handleClearCache = () => {
    queryClient.clear();
    setPreviousAccountData(null);
    if (enableFetchOverride) {
      requestCountRef.current = 0; // Reset ref counter
      setNetworkRequestCount(0); // Reset state counter
    }
    console.log("[DevTools] Cleared all cache and reset account switching state");
  };

  const handleClearStorage = () => {
    localStorage.clear();
    sessionStorage.clear();

    // Also reset the Zustand store to trigger reactive authentication check
    useAppStore.setState({
      authentication: {},
      selectedAccount: null,
      selectedCatalogue: null,
      hubspot: {},
      kyc: null,
    });

    console.log("[DevTools] Cleared all browser storage and reset store state");
  };

  // const handleForceAccountSwitch = (accountCode: string) => {
  //   if (accountCode && accountCode !== selectedAccount) {
  //     const account = myAccounts?.data?.artistaccounts?.find(
  //       (acc: Account) => acc.artistaccountcode === accountCode,
  //     );
  //     if (account != null) {
  //       useAppStore.setState({
  //         selectedAccount: account.artistaccountcode,
  //         selectedCatalogue: account.cataloguecode,
  //       });
  //       console.log("[DevTools] Force switched to account:", accountCode);
  //     }
  //   }
  // };

  const filteredAccounts: Account[] = Array.isArray(myAccounts?.data?.artistaccounts)
    ? myAccounts.data.artistaccounts
    : [];

  // Memoize expensive operations to prevent blocking the main thread
  const queryCache = useMemo(() => {
    if (!isExpanded)
      return { all: [], count: 0 };
    const allQueries = queryClient.getQueryCache().getAll();
    return {
      all: enableHeavyDebugging ? allQueries : [], // Only populate in local dev
      count: allQueries.length,
    };
  }, [queryClient, isExpanded, enableHeavyDebugging]);

  const appStateJson = useMemo(() => {
    if (!isExpanded)
      return "";
    try {
      return JSON.stringify(useAppStore.getState(), null, 2);
    }
    catch (error) {
      return `Error serializing app state: ${String(error)}`;
    }
  }, [isExpanded]);

  const uiStateJson = useMemo(() => {
    if (!isExpanded || !enableHeavyDebugging)
      return enableHeavyDebugging ? "" : "Disabled in deployed environment";
    try {
      return JSON.stringify(useUIStateStore.getState(), null, 2);
    }
    catch (error) {
      return `Error serializing UI state: ${String(error)}`;
    }
  }, [isExpanded, enableHeavyDebugging]);

  const queryCacheDetailsJson = useMemo(() => {
    if (!isExpanded || !enableHeavyDebugging)
      return enableHeavyDebugging ? "" : "Disabled in deployed environment";
    try {
      return JSON.stringify(
        queryCache.all.map(query => ({
          queryKey: query.queryKey,
          status: query.state.status,
          dataUpdatedAt: query.state.dataUpdatedAt,
          errorUpdatedAt: query.state.errorUpdatedAt,
          fetchStatus: query.state.fetchStatus,
          isInvalidated: query.state.isInvalidated,
          error: query.state.error,
        })),
        null,
        2,
      );
    }
    catch (error) {
      return `Error serializing query cache: ${String(error)}`;
    }
  }, [queryCache.all, isExpanded, enableHeavyDebugging]);

  const flagEntries = useMemo(() => {
    if (!isExpanded)
      return [];
    return Object.entries(flags);
  }, [flags, isExpanded]);

  return (
    <div className={`relative ${
      isTunecore ? "bg-layer-inverse text-white" : "bg-white text-black"
    } rounded-lg border ${
      isTunecore ? "border-stroke-subtle" : "border-gray-200"
    }`}
    >

      {/* Toggle Button */}
      <button
        type="button"
        onClick={handleToggleExpanded}
        className={`w-12 h-12 flex items-center justify-center rounded-lg hover:${
          isTunecore ? "bg-layer-inverse" : "bg-gray-100"
        }`}
        title="Developer Tools"
      >
        <Settings className="w-5 h-5" />
      </button>

      {/* Expanded Panel */}
      {isExpanded && (
        <div className={`absolute top-full md:right-0 mt-2 md:w-96 lg:w-[600px] border-t ${
          isTunecore ? "bg-layer-inverse border-stroke-subtle" : "bg-white border-gray-200"
        } rounded-lg shadow-lg z-50`}
        >
          <ScrollArea className="p-4 h-[600px]">
            <div className="space-y-4">
              <h3 className={`text-sm font-semibold ${
                isTunecore ? "text-white" : "text-gray-800"
              }`}
              >
                Dev Tools
                {" "}
                {!isLocalDev && (
                  <span className={`text-xs font-normal ${
                    isTunecore ? "text-gray-400" : "text-gray-500"
                  }`}
                  >
                    (Limited - Deployed Mode)
                  </span>
                )}
              </h3>

              {/* TuneCore Mode Toggle */}
              <div>
                <label className="text-xs">Theme Mode</label>
                <Select
                  value={FORCE_TUNECORE_MODE ? "tunecore" : "sentric"}
                  onValueChange={value => setForceTunecoreMode(value === "tunecore")}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sentric">Sentric</SelectItem>
                    <SelectItem value="tunecore">TuneCore</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Account Switch */}
              {/* {filteredAccounts.length > 0 && (
            <div className="space-y-2">
              <label className="text-xs">Force Account Switch</label>
              <Select
                value={selectedAccount ?? ""}
                onValueChange={handleForceAccountSwitch}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select account..." />
                </SelectTrigger>
                <SelectContent>
                  {filteredAccounts.map(account => (
                    <SelectItem
                      key={account.artistaccountcode}
                      value={account.artistaccountcode}
                    >
                      {account.accountname ?? account.artistaccountcode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )} */}

              {/* Action Buttons */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleClearCache}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs rounded border ${
                    isTunecore
                      ? "border-stroke-primary text-ink-accent hover:bg-action-primary hover:text-black"
                      : "border-action-secondary text-ink-accent hover:bg-action-secondary hover:text-white"
                  } transition-colors`}
                  title="Clear React Query cache and reset account switching state"
                >
                  <RefreshCw className="w-3 h-3" />
                  Clear Cache
                </button>

                <Text className="text-xs">
                  Clears React Query cache - forces fresh data fetch on next component render
                </Text>

                <button
                  type="button"
                  onClick={handleClearStorage}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs rounded border ${
                    isTunecore
                      ? "border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black"
                      : "border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
                  } transition-colors`}
                  title="Clear localStorage and sessionStorage"
                >
                  <Trash2 className="w-3 h-3" />
                  Clear Storage
                </button>
                <Text className="text-xs">
                  Resets app completely - clears all browser data, will force logout and fresh login
                </Text>

                <button
                  type="button"
                  onClick={() => {
                    void queryClient.invalidateQueries();
                    console.log("[DevTools] Invalidated all queries");
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs rounded border ${
                    isTunecore
                      ? "border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-black"
                      : "border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white"
                  } transition-colors`}
                  title="Invalidate all React Query queries (forces refetch)"
                >
                  <Database className="w-3 h-3" />
                  Invalidate Queries
                </button>
                <Text className="text-xs">
                  Softer refresh - marks data as stale, refetches happen gradually as needed
                </Text>
              </div>

              {/* Build Information */}
              {/* <div className="pt-2 border-t border-gray-200">
                <div className={`text-sm font-bold mb-2 flex items-center gap-2 ${
                  isTunecore ? "text-white" : "text-gray-800"
                }`}
                >
                  <Info className="w-4 h-4" />
                  Build Information
                </div>
                <div className="space-y-1 text-xs">
                  <div className={`flex justify-between ${
                    isTunecore ? "text-gray-300" : "text-gray-600"
                  }`}
                  >
                    <span>Build Time:</span>
                    <span className="font-mono">{new Date().toLocaleDateString()}</span>
                  </div>
                  <div className={`flex justify-between ${
                    isTunecore ? "text-gray-300" : "text-gray-600"
                  }`}
                  >
                    <span>Version:</span>
                    <span className="font-mono">{import.meta.env.VITE_APP_VERSION ?? "dev"}</span>
                  </div>
                  <div className={`flex justify-between ${
                    isTunecore ? "text-gray-300" : "text-gray-600"
                  }`}
                  >
                    <span>Environment:</span>
                    <span className="font-mono">{import.meta.env.MODE}</span>
                  </div>
                </div>
              </div> */}

              {/* State Indicators */}
              <div className="pt-2 space-y-1 border-t border-gray-200">
                <div className="space-y-2 text-xs">
                  <div className={`flex justify-between ${
                    isTunecore ? "text-gray-300" : "text-gray-600"
                  }`}
                  >
                    <span>Selected Account:</span>
                    <span className="" title={selectedAccount ?? undefined}>
                      {selectedAccount != null && selectedAccount.length > 0 ? selectedAccount.slice(-20) : "None"}
                    </span>
                  </div>
                  <div className={`flex justify-between ${
                    isTunecore ? "text-gray-300" : "text-gray-600"
                  }`}
                  >
                    <span>Artist Accounts:</span>
                    <span>{filteredAccounts.length}</span>
                  </div>
                  {enableFetchOverride && (
                    <div className={`flex justify-between ${
                      isTunecore ? "text-gray-300" : "text-gray-600"
                    }`}
                    >
                      <span>Dashboard API Calls:</span>
                      <span className={`${networkRequestCount > 50 ? "text-red-500 font-semibold" : networkRequestCount > 0 ? "text-orange-500" : ""}`}>
                        {networkRequestCount}
                      </span>
                    </div>
                  )}
                  <div className={`flex justify-between ${
                    isTunecore ? "text-gray-300" : "text-gray-600"
                  }`}
                  >
                    <span>React Queries:</span>
                    <span>{queryCache.count}</span>
                  </div>

                  {/* Expandable React Queries List - LOCAL DEV ONLY */}
                  {enableHeavyDebugging && (
                    <details className="pt-2 text-xs border-t border-gray-200">
                      <summary className={`cursor-pointer ${
                        isTunecore ? "text-gray-300" : "text-gray-600"
                      }`}
                      >
                        View Query Cache
                      </summary>
                      <div className="mt-2 space-y-1 overflow-y-auto max-h-40">
                        {queryCache.all.map((query) => {
                          const queryKey = JSON.stringify(query.queryKey);
                          const status = query.state.status;
                          const dataUpdatedAt = query.state.dataUpdatedAt
                            ? new Date(query.state.dataUpdatedAt).toLocaleTimeString()
                            : "Never";

                          return (
                            <div
                              key={queryKey}
                              className={`p-2 rounded text-xs border ${
                                isTunecore ? "border-stroke-subtle bg-layer-inverse/50" : "border-gray-200 bg-gray-50"
                              }`}
                            >
                              <div className={`font-mono text-xs truncate ${
                                status === "pending" ? "text-blue-500"
                                  : status === "error" ? "text-red-500"
                                    : status === "success" ? "text-green-500" : "text-gray-400"
                              }`}
                              >
                                {queryKey}
                              </div>
                              <div className={`text-xs mt-1 ${
                                isTunecore ? "text-gray-400" : "text-gray-500"
                              }`}
                              >
                                Status:
                                {" "}
                                {status}
                                {" "}
                                | Updated:
                                {" "}
                                {dataUpdatedAt}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  )}
                </div>
              </div>

              {/* App State */}
              <details className="text-xs">
                <summary className={`cursor-pointer ${
                  isTunecore ? "text-gray-300" : "text-gray-600"
                }`}
                >
                  App State
                </summary>
                <div className={`mt-2 p-2 rounded border max-h-40 overflow-auto ${
                  isTunecore ? "border-stroke-subtle bg-layer-inverse/50" : "border-gray-200 bg-gray-50"
                }`}
                >
                  <pre
                    className={`text-xs font-mono whitespace-pre-wrap break-all select-text overflow-auto ${
                      isTunecore ? "text-gray-300" : "text-gray-600"
                    }`}
                    style={{ wordBreak: "break-all" }}
                  >
                    {appStateJson}
                  </pre>
                </div>
              </details>

              {/* UI State - LOCAL DEV ONLY */}
              {enableHeavyDebugging && (
                <details className="text-xs">
                  <summary className={`cursor-pointer ${
                    isTunecore ? "text-gray-300" : "text-gray-600"
                  }`}
                  >
                    UI State
                  </summary>
                  <div className={`mt-2 p-2 rounded border max-h-40 overflow-auto ${
                    isTunecore ? "border-stroke-subtle bg-layer-inverse/50" : "border-gray-200 bg-gray-50"
                  }`}
                  >
                    <pre
                      className={`text-xs font-mono whitespace-pre-wrap break-all select-text overflow-auto ${
                        isTunecore ? "text-gray-300" : "text-gray-600"
                      }`}
                      style={{ wordBreak: "break-all" }}
                    >
                      {uiStateJson}
                    </pre>
                  </div>
                </details>
              )}

              {/* Query Cache Details - LOCAL DEV ONLY */}
              {enableHeavyDebugging && (
                <details className="text-xs">
                  <summary className={`cursor-pointer ${
                    isTunecore ? "text-gray-300" : "text-gray-600"
                  }`}
                  >
                    Query Cache Details
                  </summary>
                  <div className={`mt-2 p-2 rounded border max-h-40 overflow-auto ${
                    isTunecore ? "border-stroke-subtle bg-layer-inverse/50" : "border-gray-200 bg-gray-50"
                  }`}
                  >
                    <pre
                      className={`text-xs font-mono whitespace-pre-wrap break-all select-text overflow-auto ${
                        isTunecore ? "text-gray-300" : "text-gray-600"
                      }`}
                      style={{ wordBreak: "break-all" }}
                    >
                      {queryCacheDetailsJson}
                    </pre>
                  </div>
                </details>
              )}

              {/* Flags */}
              <details className="text-xs">
                <summary className={`cursor-pointer ${
                  isTunecore ? "text-gray-300" : "text-gray-600"
                }`}
                >
                  Flags
                </summary>
                <div className="grid grid-cols-1 gap-1 mt-2 overflow-y-auto text-xs max-h-32">
                  {flagEntries.length > 0 && flagEntries.map(([key, value]) => (
                    <div
                      key={key}
                      className={`flex items-center justify-between ${
                        isTunecore ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      <span className="truncate">
                        {key}
                        :
                      </span>
                      <span className={`font-mono ${value !== null && value !== undefined && value !== false ? "text-green-500" : "text-red-500"}`}>
                        {String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
